import { useMemo, useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import {
  useArchiveUnit,
  useCreateUnit,
  useUnits,
  useUpdateUnit,
} from './hooks/useCatalogQueries';
import type { Unit } from './types';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';

const UNIT_CATEGORIES = [
  { value: 'length', label: 'Длина' },
  { value: 'velocity', label: 'Скорость' },
  { value: 'volume_per_area', label: 'Объём/площадь' },
  { value: 'mass_per_area', label: 'Масса/площадь' },
  { value: 'density', label: 'Плотность' },
  { value: 'temperature', label: 'Температура' },
  { value: 'ratio', label: 'Доля' },
  { value: 'time', label: 'Время' },
] as const;

const categoryLabel = (cat: string): string =>
  UNIT_CATEGORIES.find((c) => c.value === cat)?.label ?? cat;

interface Draft {
  code: string;
  symbol: string;
  ru: string;
  category: string;
  display_order: number;
}

const emptyDraft = (category: string): Draft => ({
  code: '',
  symbol: '',
  ru: '',
  category,
  display_order: 0,
});

const draftValid = (d: Draft): boolean =>
  /^[a-z0-9_]+$/.test(d.code.trim()) &&
  d.symbol.trim().length > 0 &&
  d.ru.trim().length > 0 &&
  Boolean(d.category);

export function PlatformAdminUnitsPage() {
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const unitsQ = useUnits(categoryFilter === 'all' ? undefined : categoryFilter);
  const createMut = useCreateUnit();
  const updateMut = useUpdateUnit();
  const deleteMut = useArchiveUnit();

  const items = unitsQ.data ?? [];

  const [creating, setCreating] = useState<Draft | null>(null);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(emptyDraft('length'));

  const categoryOptions = useMemo(
    () => UNIT_CATEGORIES.map((c) => ({ value: c.value, label: c.label })),
    [],
  );

  const startCreate = () =>
    setCreating(emptyDraft(categoryFilter !== 'all' ? categoryFilter : 'length'));

  const startEdit = (u: Unit) => {
    setEditingCode(u.code);
    setEditDraft({
      code: u.code,
      symbol: u.symbol,
      ru: u.name_translations.ru ?? '',
      category: u.category,
      display_order: u.display_order,
    });
  };

  const saveCreate = async () => {
    if (!creating || !draftValid(creating)) return;
    // AK-574: без catch отказ (409 «код занят», 422) уходил в unhandled
    // rejection — строка оставалась в режиме правки без единого слова.
    try {
      await createMut.mutateAsync({
        code: creating.code.trim(),
        symbol: creating.symbol.trim(),
        name_translations: creating.ru.trim() ? { ru: creating.ru.trim() } : {},
        category: creating.category,
        display_order: creating.display_order,
      });
      toast.success('Единица добавлена');
      setCreating(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  const saveEdit = async () => {
    if (!editingCode || !draftValid(editDraft)) return;
    try {
      await updateMut.mutateAsync({
        code: editingCode,
        payload: {
          symbol: editDraft.symbol.trim(),
          name_translations: editDraft.ru.trim() ? { ru: editDraft.ru.trim() } : {},
          category: editDraft.category,
          display_order: editDraft.display_order,
        },
      });
      toast.success('Единица обновлена');
      setEditingCode(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  const handleDelete = async (u: Unit) => {
    if (!window.confirm(`Удалить «${u.symbol}» (${u.code})?`)) return;
    try {
      await deleteMut.mutateAsync(u.code);
      toast.success('Единица удалена');
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  return (
    <PlatformAdminLayout>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Единицы измерения</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Мини-справочник единиц · {items.length} записей
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все категории</SelectItem>
              {categoryOptions.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={startCreate} disabled={Boolean(creating)}>
            <Plus className="h-4 w-4" />
            Добавить
          </Button>
        </div>
      </div>

      <Card className="mt-5">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead style={{ width: '160px' }}>Категория</TableHead>
                <TableHead style={{ width: '140px' }}>Код</TableHead>
                <TableHead style={{ width: '100px' }}>Символ</TableHead>
                <TableHead>Название (RU)</TableHead>
                <TableHead style={{ width: '100px' }}>Порядок</TableHead>
                <TableHead style={{ width: '110px' }} className="text-right">
                  Действия
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {unitsQ.isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {creating && (
                <TableRow>
                  <TableCell>
                    <Select
                      value={creating.category}
                      onValueChange={(v) => setCreating({ ...creating, category: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categoryOptions.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={creating.code}
                      onChange={(e) => setCreating({ ...creating, code: e.target.value })}
                      placeholder="kg_per_ha"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={creating.symbol}
                      onChange={(e) => setCreating({ ...creating, symbol: e.target.value })}
                      placeholder="кг/га"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={creating.ru}
                      onChange={(e) => setCreating({ ...creating, ru: e.target.value })}
                      placeholder="Килограмм на гектар"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      value={creating.display_order}
                      onChange={(e) =>
                        setCreating({ ...creating, display_order: Number(e.target.value) })
                      }
                      className="w-20"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        onClick={() => void saveCreate()}
                        disabled={!draftValid(creating)}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setCreating(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {items.map((u) =>
                editingCode === u.code ? (
                  <TableRow key={u.code}>
                    <TableCell>
                      <Select
                        value={editDraft.category}
                        onValueChange={(v) => setEditDraft({ ...editDraft, category: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categoryOptions.map((c) => (
                            <SelectItem key={c.value} value={c.value}>
                              {c.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs text-muted-foreground">{u.code}</code>
                    </TableCell>
                    <TableCell>
                      <Input
                        value={editDraft.symbol}
                        onChange={(e) => setEditDraft({ ...editDraft, symbol: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={editDraft.ru}
                        onChange={(e) => setEditDraft({ ...editDraft, ru: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={editDraft.display_order}
                        onChange={(e) =>
                          setEditDraft({ ...editDraft, display_order: Number(e.target.value) })
                        }
                        className="w-20"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          onClick={() => void saveEdit()}
                          disabled={!draftValid(editDraft)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingCode(null)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={u.code}>
                    <TableCell>
                      <Badge variant="outline">{categoryLabel(u.category)}</Badge>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs text-muted-foreground">{u.code}</code>
                    </TableCell>
                    <TableCell className="font-medium text-foreground">{u.symbol}</TableCell>
                    <TableCell className="text-foreground">
                      {u.name_translations.ru ?? u.code}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">{u.display_order}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(u)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void handleDelete(u)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </PlatformAdminLayout>
  );
}

export default PlatformAdminUnitsPage;
